import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { isSSR } from '../utils/is-ssr';

export type TbtSchedulePriority = 'high' | 'medium' | 'low';

class TbtScheduler {
  private _highQueue: Array<() => void> = [];
  private _mediumQueue: Array<() => void> = [];
  private _lowQueue: Array<() => void> = [];
  private _isScheduled: boolean = false;

  schedule(task: () => void, priority: TbtSchedulePriority = 'low') {
    switch (priority) {
      case 'high':
        this._highQueue.push(task);
        break;
      case 'medium':
        this._mediumQueue.push(task);
        break;
      case 'low':
        this._lowQueue.push(task);
        break;
    }
    this._runNext();
  }

  private _hasPendingTasks(): boolean {
    return this._highQueue.length > 0 || this._mediumQueue.length > 0 || this._lowQueue.length > 0;
  }

  private _dequeueNext(): (() => void) | undefined {
    return this._highQueue.shift() ?? this._mediumQueue.shift() ?? this._lowQueue.shift();
  }

  private _runNext() {
    if (this._isScheduled || !this._hasPendingTasks()) {
      return;
    }

    this._isScheduled = true;

    const runTask = (): void => {
      const task: (() => void) | undefined = this._dequeueNext();
      if (task) {
        try {
          task();
        } catch (e) {
          console.error('Task failed', e);
        }
      }

      this._isScheduled = false;
      this._runNext();
    };

    if ('requestAnimationFrame' in globalThis) {
      requestAnimationFrame(() => {
        setTimeout(() => runTask(), 0);
      });
    } else {
      // unit-test case
      runTask();
    }
  }
}
function provideTbtScheduler(): TbtScheduler {
  return provideSingleton(TbtScheduler, () => new TbtScheduler());
}

export function tbtSchedule(run: () => void, priority: TbtSchedulePriority = 'low'): void {
  if (isSSR()) {
    throw new Error('PLEASE DONT USE SCHEDULER ON SERVER SIDE MODE SSR');
  }
  if (!__RENDER_LIGHTHOUSE_TBT_SHEDULER__) {
    run();
    return;
  }
  provideTbtScheduler().schedule(() => run(), priority);
}

export function tbtVisualSyncSchedule(run: () => void): void {
  if (isSSR()) {
    throw new Error('PLEASE DONT USE SCHEDULER ON SERVER SIDE MODE SSR');
  }
  if (!__RENDER_LIGHTHOUSE_TBT_SHEDULER__) {
    run();
    return;
  }
  setTimeout(() => {
    requestAnimationFrame(() => {
      run();
    });
  }, 0);
}

export function tbtIdleSchedule(run: () => void): void {
  if (isSSR()) {
    throw new Error('PLEASE DONT USE SCHEDULER ON SERVER SIDE MODE SSR');
  }
  if (!__RENDER_LIGHTHOUSE_TBT_SHEDULER__) {
    run();
    return;
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => run());
  } else {
    provideTbtScheduler().schedule(() => run());
  }
}
